import { useMemo, useState } from 'react'
import { Download, FilePlus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { fmtDate, fmtIsoDate } from '@/lib/dates'
import { useBackorders, useResolveBackorder, useUpdateBackorder } from '@/hooks/useBackorders'
import { useUIStore, type POLineItemPrefill } from '@/store/uiStore'
import type { components } from '@/lib/api/generated'

type BackorderRow = components['schemas']['BackorderItemResponse']

const REASON_LABEL: Record<string, string> = {
  removed_from_po: 'Removed from PO',
  unreceived_on_archive: 'Unreceived (archived)',
}

function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return
  const cols = Object.keys(rows[0])
  const escape = (v: unknown) => {
    const s = v == null ? '' : String(v)
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s
  }
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => escape(r[c])).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function QtyCell({ row }: { row: BackorderRow }) {
  const update = useUpdateBackorder()
  const [val, setVal] = useState(String(row.qty))
  const commit = () => {
    const n = parseInt(val, 10)
    if (Number.isNaN(n) || n === row.qty) {
      setVal(String(row.qty))
      return
    }
    update.mutate({ id: row.id, qty: n })
  }
  return (
    <Input
      type="number"
      min={0}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      className="h-7 w-16 text-right text-xs tabular-nums"
    />
  )
}

function NotesCell({ row }: { row: BackorderRow }) {
  const update = useUpdateBackorder()
  const [val, setVal] = useState(row.notes ?? '')
  const commit = () => {
    if (val === (row.notes ?? '')) return
    update.mutate({ id: row.id, notes: val })
  }
  return (
    <Input
      value={val}
      placeholder="—"
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      className="h-7 w-full min-w-[120px] text-xs"
    />
  )
}

export function BackorderTab() {
  const [showResolved, setShowResolved] = useState(false)
  const { data: rows = [], isLoading } = useBackorders(showResolved ? 'ALL' : 'OPEN')
  const resolve = useResolveBackorder()
  const setActiveModal = useUIStore((s) => s.setActiveModal)

  // Row selection for "Create PO from selected".
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const toggleRow = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const allIds = rows.map((r) => r.id)
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id))
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds))

  // Group by brand (vendor) for a per-brand view, brands A→Z.
  const groups = useMemo(() => {
    const byVendor = new Map<string, BackorderRow[]>()
    for (const r of rows) {
      const key = r.vendor || '(no brand)'
      if (!byVendor.has(key)) byVendor.set(key, [])
      byVendor.get(key)!.push(r)
    }
    return Array.from(byVendor.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [rows])

  const handleExport = () => {
    if (!rows.length) return
    const date = fmtIsoDate(new Date().toISOString())
    downloadCsv(
      `backorders-${date}.csv`,
      rows.map((r) => ({
        Brand: r.vendor,
        Item: r.product_title,
        Variant: r.variant_title,
        SKU: r.sku,
        Barcode: r.barcode,
        Qty: r.qty,
        'Source PO': r.source_po_id ?? '',
        Reason: REASON_LABEL[r.reason ?? ''] ?? r.reason ?? '',
        'Date added': fmtIsoDate(r.created_date),
        Status: r.status,
        Notes: r.notes,
      }))
    )
  }

  const handleResolve = (row: BackorderRow) => {
    if (!window.confirm(`Delete this backorder row for ${row.product_title}?`)) return
    resolve.mutate(row.id, {
      onSuccess: () => toast.success('Backorder deleted'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed'),
    })
  }

  // Open the Create PO wizard pre-loaded with the selected backorder rows.
  // The backordered qty becomes each line's suggested order quantity; the
  // wizard pre-seeds the vendor when all selected rows share one brand, and
  // the user picks the recipient + receive location and reviews before saving.
  // Backorder rows are left as-is (the draft might be discarded) — resolve
  // them once the PO is actually placed.
  const handleCreatePO = () => {
    const chosen = rows.filter((r) => selected.has(r.id))
    if (!chosen.length) return
    const prefill: POLineItemPrefill[] = chosen.map((r) => ({
      variant_id: r.variant_id,
      product_id: r.product_id,
      product_title: r.product_title,
      variant_title: r.variant_title,
      sku: r.sku,
      barcode: r.barcode,
      vendor: r.vendor,
      unit_cost: r.cost_base,
      recommended_qty: r.qty,
    }))
    setActiveModal('create', undefined, prefill)
    setSelected(new Set())
  }

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-3 border-b border-[var(--line-warm)] px-4 py-2">
        <span className="text-xs text-[var(--mute)]">
          {rows.length} {rows.length === 1 ? 'item' : 'items'}
        </span>
        <label className="flex items-center gap-1.5 text-xs text-[var(--ink-2)]">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          Show deleted
        </label>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={handleCreatePO}
          disabled={selected.size === 0}
          title={
            selected.size === 0
              ? 'Select one or more rows to create a PO'
              : `Create a PO from ${selected.size} selected`
          }
          data-testid="create-po-from-selected"
        >
          <FilePlus className="mr-1 h-3 w-3" />
          Create PO from selected
          {selected.size > 0 && (
            <span className="ml-0.5 text-[10px] text-[var(--mute)]">({selected.size})</span>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={handleExport}
          disabled={!rows.length}
        >
          <Download className="mr-1 h-3 w-3" />
          Export
        </Button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {isLoading && <div className="p-6 text-sm text-[var(--mute)]">Loading…</div>}

        {!isLoading && rows.length === 0 && (
          <div className="p-10 text-center text-sm text-[var(--mute)]">
            No backorders. Items land here when they're removed from a placed PO
            or left unreceived when a partial PO is archived.
          </div>
        )}

        {!isLoading && rows.length > 0 && (
          <table className="w-full border-collapse text-xs">
            <thead className="sticky top-0 z-10 bg-[var(--surface-header)]">
              <tr className="border-b border-[var(--line-warm)] text-left text-[var(--mute)]">
                <th className="tbl-th w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all backorders"
                  />
                </th>
                <th className="tbl-th">Item</th>
                <th className="tbl-th">SKU</th>
                <th className="tbl-th">Barcode</th>
                <th className="tbl-th text-right">Qty</th>
                <th className="tbl-th">Source PO</th>
                <th className="tbl-th">Reason</th>
                <th className="tbl-th">Date added</th>
                <th className="tbl-th">Notes</th>
                <th className="tbl-th" />
              </tr>
            </thead>
            <tbody>
              {groups.map(([vendor, items]) => (
                <FragmentGroup
                  key={vendor}
                  vendor={vendor}
                  items={items}
                  selected={selected}
                  onToggleRow={toggleRow}
                  onResolve={handleResolve}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function FragmentGroup({
  vendor,
  items,
  selected,
  onToggleRow,
  onResolve,
}: {
  vendor: string
  items: BackorderRow[]
  selected: Set<number>
  onToggleRow: (id: number) => void
  onResolve: (row: BackorderRow) => void
}) {
  return (
    <>
      <tr className="bg-[var(--surface-header)]">
        <td
          colSpan={10}
          className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--ink-2)]"
        >
          {vendor} <span className="text-[var(--mute)]">({items.length})</span>
        </td>
      </tr>
      {/* Zebra keyed on the row index within the vendor group (like
          POTable), so the vendor header rows don't flip the pattern. */}
      {items.map((row, index) => (
        <tr
          key={row.id}
          className={cn(
            'border-b border-[var(--line-warm)] hover:bg-[var(--surface-header)]',
            index % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
          )}
        >
          <td className="px-3 py-2">
            <input
              type="checkbox"
              checked={selected.has(row.id)}
              onChange={() => onToggleRow(row.id)}
              aria-label={`Select ${row.product_title}`}
            />
          </td>
          <td className="px-3 py-2">
            <span className="font-medium text-[var(--text-primary)]">{row.product_title}</span>
            {row.variant_title && (
              <span className="text-[var(--mute)]"> · {row.variant_title}</span>
            )}
            {row.status === 'RESOLVED' && (
              <span className="ml-1.5 text-[10px] font-semibold uppercase text-[var(--mute)]">
                · Deleted
              </span>
            )}
          </td>
          <td className="px-3 py-2 text-[var(--ink-2)]">{row.sku || '—'}</td>
          <td className="px-3 py-2 text-[var(--ink-2)]">{row.barcode || '—'}</td>
          <td className="px-3 py-2 text-right tabular-nums">
            {row.status === 'OPEN' ? (
              <QtyCell row={row} />
            ) : (
              <span className="tabular-nums">{row.qty}</span>
            )}
          </td>
          <td className="px-3 py-2 text-[var(--ink-2)]">{row.source_po_id ?? '—'}</td>
          <td className="px-3 py-2 text-[var(--ink-2)]">
            {REASON_LABEL[row.reason ?? ''] ?? row.reason ?? '—'}
          </td>
          <td className="px-3 py-2 tabular-nums whitespace-nowrap text-[var(--ink-2)]">
            {fmtDate(row.created_date, '—')}
          </td>
          <td className="px-3 py-2">
            {row.status === 'OPEN' ? (
              <NotesCell row={row} />
            ) : (
              <span className="text-[var(--ink-2)]">{row.notes || '—'}</span>
            )}
          </td>
          <td className="px-3 py-2 text-right">
            {row.status === 'OPEN' && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => onResolve(row)}
              >
                Delete
              </Button>
            )}
          </td>
        </tr>
      ))}
    </>
  )
}
